import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEditPlan } from '@hooks/useEditPlan/useEditPlan';
import { useDeleteCartItem } from '@hooks/useDeleteCartItem/useDeleteCartItem';
import { addChildItem } from '@services/shoppingCartService';
import { isCouponItem } from '@hooks/useShoppingCart/coupon';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useCartSummary } from '@hooks/useCartSummary';
import { useCartEquipment } from '@hooks/useCartEquipment/useCartEquipment';
import { useEmptyCart } from '@hooks/useEmptyCart';
import { useProductOfferings } from '@hooks/useProductOfferings/useProductOfferings';
import { useEvOfferings } from '@hooks/useEvOfferings/useEvOfferings';
import { useTenantConfig } from '@hooks/useTenantConfig/useTenantConfig';
import { useReleaseOnCartEmpty } from '@hooks/useReleaseOnCartEmpty';
import { getCategoryFromItemName } from '@utils/cartUtils';
import { useSessionContext } from '@contexts/SessionContext';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import type { CartItemDTO, ShoppingCartDTO } from '@t/api.types';
import type { CategoryType, Plan } from '@t/productOffering.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SORT_ORDER: Record<string, number> = { Internet: 0, Video: 1, Phone: 2 };

export const findMatchingPlan = (item: CartItemDTO, plansByCategory: Record<CategoryType, Plan[]>): Plan | null => {
  const category = getCategoryFromItemName(item.productOffering?.name);
  if (category) {
    const plans = plansByCategory[category] ?? [];
    const found =
      plans.find((p) => p.planId === item.productOffering?.id) ??
      plans.find((p) => p.name === item.productOffering?.name) ??
      null;
    if (found) return found;
  }
  // Fallback: search all categories — bundle plans won't match the name-based category heuristic
  const allPlans = Object.values(plansByCategory).flat();
  return (
    allPlans.find((p) => p.planId === item.productOffering?.id) ??
    allPlans.find((p) => p.name === item.productOffering?.name) ??
    null
  );
};

export const sortCartItems = (items: CartItemDTO[]): CartItemDTO[] =>
  [...items].sort((a, b) => {
    const catA = getCategoryFromItemName(a.productOffering?.name);
    const catB = getCategoryFromItemName(b.productOffering?.name);
    return (SORT_ORDER[catA ?? ''] ?? 9) - (SORT_ORDER[catB ?? ''] ?? 9);
  });

export const resolveCartItemPlan = (
  item: CartItemDTO,
  plansByCategory: Record<CategoryType, Plan[]>,
  evPlans: Plan[]
): Plan | null => {
  const telcoPlan = findMatchingPlan(item, plansByCategory);
  if (telcoPlan) return telcoPlan;
  return (
    evPlans.find((p) => p.planId === item.productOffering?.id) ??
    evPlans.find((p) => p.name === item.productOffering?.name) ??
    null
  );
};

// ---------------------------------------------------------------------------
// Resolved cart item (pre-computed for rendering)
// ---------------------------------------------------------------------------

export interface ResolvedCartItem {
  item: CartItemDTO;
  plan: Plan | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useShoppingCartDrawer = () => {
  const { sessionId } = useSessionContext();
  const queryClient = useQueryClient();
  const resolvedAddressId = useQualificationFlowStore((s) => s.resolvedAddressId);
  const editPlan = useEditPlan(sessionId ?? '');

  // Direct query-hook reads (slice-10 prop diet): TanStack Query's cache
  // dedupes these against the page-level observers.
  const {
    cart,
    isCartLoading,
    isPending: isCartMutating,
    removePlanAsync,
    removeAddOnsAsync,
  } = useShoppingCart(sessionId ?? '');
  const { plansByCategory } = useProductOfferings(sessionId ?? '', resolvedAddressId ?? '');
  const tenantConfig = useTenantConfig();
  const { data: evPlans = [] } = useEvOfferings(sessionId ?? '', tenantConfig.data?.hasEvOfferings ?? false);
  const equipmentByPlanId = useCartEquipment(sessionId ?? '', resolvedAddressId ?? '', cart);
  const { emptyCart } = useEmptyCart(sessionId ?? '');
  const { releaseIfCartEmpty } = useReleaseOnCartEmpty();

  // -- Dialog state ----------------------------------------------------------

  const [removeAllDialogOpen, setRemoveAllDialogOpen] = useState(false);
  const [detailsPlan, setDetailsPlan] = useState<Plan | null>(null);

  // -- Remove flows -----------------------------------------------------------

  const handleRemovePlan = async (offeringId: string) => {
    await removePlanAsync(offeringId);
    releaseIfCartEmpty();
  };

  const handleRemoveAllItems = () => {
    void emptyCart();
  };

  // -- Delete flow (shared with ProductReviewStep) --------------------------

  const { handleDeleteItem, deleteInternetDialogOpen, closeDeleteInternetDialog, confirmDeleteInternet } =
    useDeleteCartItem({ cart, onRemoveItem: handleRemovePlan, onRemoveAllItems: handleRemoveAllItems });

  // -- Derived data ----------------------------------------------------------

  const items = (cart?.cartItem ?? []).filter((item) => !isCouponItem(item));
  const hasItems = items.length > 0;

  const sortedItems: ResolvedCartItem[] = sortCartItems(items).map((item) => ({
    item,
    plan: resolveCartItemPlan(item, plansByCategory, evPlans),
  }));

  const { monthlyTotal, oneTimeTotal } = useCartSummary(sessionId ?? '');
  const dueToday = monthlyTotal + oneTimeTotal;

  // -- Child quantity update -------------------------------------------------

  // Search top-level items first, then their children (bundle member equipment is a grandchild).
  const findParentItem = (cartItems: CartItemDTO[] | undefined, childItemId: string): CartItemDTO | undefined => {
    const directParent = cartItems?.find((ci) => ci.childItems?.some((child) => child.id === childItemId));
    if (directParent) return directParent;

    return (cartItems ?? [])
      .flatMap((topItem) => topItem.childItems ?? [])
      .find((member) => (member.childItems ?? []).some((c) => c.id === childItemId));
  };

  const addQuantity = async (parentOfferingCode: string, offeringId: string, offeringName: string, quantity: number) =>
    sessionId
      ? addChildItem(sessionId, parentOfferingCode, [
          { action: 'add', quantity, productOffering: { id: offeringId, name: offeringName } },
        ])
      : undefined;

  // Equipment is stored as a SINGLE row carrying its own count, and the backend's
  // same-category cascade replaces that row on an add — so set the count directly to
  // the target N (works for both increase and decrease). Add-ons keep the
  // one-row-per-unit model, adding or removing only the delta.
  const applyQuantityChange = async (
    matchingChildren: CartItemDTO[],
    parentOfferingCode: string,
    offeringId: string,
    offeringName: string,
    newQuantity: number
  ): Promise<ShoppingCartDTO | undefined> => {
    if (matchingChildren.some((child) => child.type === 'equipment')) {
      return addQuantity(parentOfferingCode, offeringId, offeringName, newQuantity);
    }

    const delta = newQuantity - matchingChildren.length;
    if (delta > 0) {
      return addQuantity(parentOfferingCode, offeringId, offeringName, delta);
    }
    if (delta < 0) {
      const idsToRemove = matchingChildren.slice(0, Math.abs(delta)).map((child) => child.id!);
      if (idsToRemove.length > 0) {
        await removeAddOnsAsync(idsToRemove);
      }
    }
    return undefined;
  };

  const handleUpdateChildQuantity = async (
    childItemId: string,
    offeringId: string,
    offeringName: string,
    newQuantity: number
  ) => {
    if (!childItemId || !sessionId) return;

    const parentItem = findParentItem(cart?.cartItem, childItemId);
    const parentOfferingCode = parentItem?.productOffering?.id;
    if (!parentOfferingCode) return;

    const matchingChildren =
      parentItem?.childItems?.filter((child) => child.productOffering?.id === offeringId && child.id) ?? [];

    const updatedCart = await applyQuantityChange(matchingChildren, parentOfferingCode, offeringId, offeringName, newQuantity);

    if (updatedCart) {
      queryClient.setQueryData(['shoppingCart', sessionId], updatedCart);
    }
  };

  // -- Delete handling (delegated to useDeleteCartItem) ----------------------

  const confirmRemoveAll = () => {
    setRemoveAllDialogOpen(false);
    handleRemoveAllItems();
  };

  // -- View details ----------------------------------------------------------

  const handleViewDetails = (plan: Plan | null) => {
    if (plan) setDetailsPlan(plan);
  };

  const closeDetails = () => setDetailsPlan(null);

  return {
    // Cart state (read directly from the query hooks)
    cart,
    isCartLoading,
    isCartMutating,
    equipmentByPlanId,
    plansByCategory,
    sessionId,

    // Resolved data
    sortedItems,
    hasItems,
    monthlyTotal,
    oneTimeTotal,
    dueToday,

    // Child quantity
    handleUpdateChildQuantity,

    // Delete flow
    handleDeleteItem,
    removeAllDialogOpen,
    setRemoveAllDialogOpen,
    deleteInternetDialogOpen,
    closeDeleteInternetDialog,
    confirmDeleteInternet,
    confirmRemoveAll,

    // Details modal
    detailsPlan,
    handleViewDetails,
    closeDetails,

    // Edit plan
    editPlan,
  };
};
